// @flow
import React, {useEffect, useRef, useState} from 'react';
import {useLocation, useHistory} from 'react-router-dom';
import {makeStyles} from '@material-ui/core';
import queryString from 'query-string';

import {useAuthSession, useUser, useUserType} from '../modules/auth/utils';
import {useStoreInfo, useStorePreviousTier} from '../modules/store/utils';
import {useTier22Modal} from '../modules/ui/utils';
import {useListenNotifications} from '../modules/store-notifications/utils/real-time';
import {
  useInitializeTaxInvoiceSetting,
  useTaxInvoiceList,
} from '../modules/tax-invoice/utils';
import {useAnalyticsTracking} from '../hooks/use-analytics-tracking';

import SidebarContainer from '../modules/sidebar/containers/SidebarContainer';
import HeaderBar from '../elements/HeaderBar';
import Toaster from '../modules/toast-alert/components';
import {ScrollToTop} from '../utils/react';
import {classNames} from '../utils/react/className';

import {actions as checkoutActions} from '../modules/checkout/duck';

// Modals
import SubscriptionModal from '../modules/tiers/components/SubscriptionModal';
import TierRestrictionModalAlert from '../modules/TierRestriction/components';
import TiersListModal from '../modules/tiers/components/TiersListModal/TiersListModal';
import NoUserPermissionsModal from '../modules/configure/accounts/components/Modals/NoUserPermissionsModal';
import PlanExpiredModal from '../modules/checkout/components/Modals/PlanExpiredModal';
import PaymentSuccessModal from '../modules/checkout/components/Modals/PaymentSuccessModal';
import PaymentFailureModal from '../modules/checkout/components/Modals/PaymentFailureModal';
import StoreChurnModal from '../modules/checkout/components/Modals/StoreChurnModal';
import DomainChangedModal from '../elements/DomainChangedModal/DomainChangedModal';

import environment from '../environment';
import {theme} from '../theme';
import {useCurrentSubscription} from '../modules/tiers/utils';
import {ON_BOARDING_TYPE} from '../modules/on-boarding/utils/constants';
import {useActions} from '../utils/globalStore';
import {VERSION_APP_HEIGHT} from './constants';

const UPGRADE_PATHS = ['/tiers-list', '/checkout', '/checkout/payment'];

const useStyles = makeStyles({
  root: {
    position: 'relative',
    width: '100%',
    background: '#F8F9FB',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      maxWidth: '100%',
      width: '1px',
      flexGrow: 1,
      transition: 'all 0.3s ease-in-out',
    },
    minHeight: '100%',
    height: 'fit-content',
  },
  tier11Container: {
    [theme.breakpoints.down('sm')]: {
      paddingBottom: 106,
    },
  },
  version: {
    fontSize: '0.75rem',
    color: '#6B7280',
    textAlign: 'center',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: `${VERSION_APP_HEIGHT}px`,
  },
});

type Props = {
  children: React$Node,
};

function AuthenticatedLayout({children}: Props) {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const rootContainerRef = useRef(null);
  const currentSubscription = useCurrentSubscription();

  const {refreshSession} = useAuthSession();
  const freeTrialSubscribe = useActions(checkoutActions.freeTrialSubscribe);

  const user = useUser();
  const {isVendor} = useUserType();
  const store = useStoreInfo();
  const storePreviousTier = useStorePreviousTier();
  const {isOpen: tier22ModalOpen, close: closeTier22Modal} = useTier22Modal();

  useAnalyticsTracking();
  useListenNotifications();
  useInitializeTaxInvoiceSetting();
  useTaxInvoiceList();

  const queryObject = queryString.parse(location.search);
  const s_u_token = queryObject.s_u_token;
  const [domainChangedModal, setDomainChangedModal] = useState(false);

  useEffect(() => {
    if (queryObject.d_c_token) setDomainChangedModal(true);
  }, [queryObject.d_c_token]);

  const handleCloseDomainChangedModal = () => {
    setDomainChangedModal(false);
    history.replace({search: ''});
  };

  useEffect(() => {
    if (rootContainerRef.current) {
      rootContainerRef.current.scrollTo(0, 0);
    }
  }, [location.pathname]);

  // Auto subscribe to free trial
  useEffect(() => {
    if (
      store &&
      store.initialConfig &&
      ['TIER_110', 'TIER_0'].includes(store.storeTier.name) &&
      store.onboardingTypeId === ON_BOARDING_TYPE.FREE_TRIAL &&
      currentSubscription === null &&
      !s_u_token
    ) {
      freeTrialSubscribe({
        freeTrialSubscribeRequest: {
          storeTierId: '350',
          frequency: 'SEMIANNUALLY',
        },
      });
    }
  }, [
    currentSubscription,
    freeTrialSubscribe,
    refreshSession,
    s_u_token,
    store,
  ]);

  const storeTierName = store?.storeTier?.name;
  const isTier11Container =
    storeTierName === 'TIER_11' ||
    (storeTierName === 'TIER_22' && storePreviousTier?.name === 'TIER_11');

  return (
    <>
      <SidebarContainer />

      <SubscriptionModal />
      <TierRestrictionModalAlert />
      <TiersListModal />
      <NoUserPermissionsModal />
      <Toaster />
      <ScrollToTop />

      {isVendor && (
        <>
          <PlanExpiredModal />
          {UPGRADE_PATHS.includes(location.pathname) && (
            <StoreChurnModal
              open={!!tier22ModalOpen}
              handleClose={closeTier22Modal}
            />
          )}
          <PaymentSuccessModal />
          <PaymentFailureModal />
        </>
      )}

      {user && domainChangedModal && (
        <DomainChangedModal
          open={domainChangedModal}
          user={user}
          handleClose={handleCloseDomainChangedModal}
        />
      )}

      <div
        ref={rootContainerRef}
        className={classNames(classes.root, {
          [classes.tier11Container]: isTier11Container,
        })}
      >
        <HeaderBar />
        {children}
        <aside className={classes.version}>
          <span>Versión {environment.version}</span>
        </aside>
      </div>
    </>
  );
}

export default AuthenticatedLayout;
